
import useFormattedDateTime from '@/mixins/useFormattedDateTime';
import { LegalType, SettingsLegal } from '@/models/Settings';
import { getDownload } from '@/services/Settings/Api';
import downloadFile from '@/utils/downloadFile';
import {
  computed, defineComponent, PropType, ref
} from 'vue';
import useI18n from '@/mixins/useI18n';
import useTriggerLegalTextsPopup from '@/mixins/useTriggerLegalTextsPopup';
import EditIcon from '@/components/molecules/EditIcon.vue';

export default defineComponent({
  name: 'LegalButton',
  components: { EditIcon },
  props: {
    legalObject: {
      type: Object as PropType<SettingsLegal | null>,
      default: null
    },
    isCurrent: Boolean,
    usePopup: Boolean
  },
  setup(props) {
    const { getFormattedDate } = useFormattedDateTime();
    const { t } = useI18n();
    const { setLegalText } = useTriggerLegalTextsPopup();

    const typeKey = computed(() => {
      switch (props.legalObject?.type) {
        case LegalType.LegalTerms:
          return 'terms';
        case LegalType.Privacy:
          return 'privacy';
        case LegalType.ServiceUsageAgreement:
          return 'serviceusageagreement';
        case LegalType.PriceList:
          return 'pricelist';
        default:
          return '?';
      }
    });

    const isDownloading = ref(false);

    const download = async () => {
      let downloadType = '';
      switch (props.legalObject?.type) {
        case LegalType.LegalTerms:
          downloadType = 'legalterms';
          break;
        case LegalType.Privacy:
          downloadType = 'privacy';
          break;
        case LegalType.ServiceUsageAgreement:
          downloadType = 'serviceusageagreement';
          break;
        case LegalType.PriceList:
          downloadType = 'pricelist';
          break;
        default:
          throw new Error('Invalid legal type');
      }

      isDownloading.value = true;
      const data = await getDownload(downloadType as 'privacy' | 'legalterms' | 'serviceusageagreement' | 'pricelist');
      if (data) {
        downloadFile(data.attachment, data.filename, data.fileType);
      }
      isDownloading.value = false;
    };

    const version = computed(() => {
      if (props.isCurrent) return t('settings.legal.current_version');
      return `${t('settings.legal.version')} ${props.legalObject?.version} (${getFormattedDate(props.legalObject?.acceptedDate)})`;
    });

    const onClick = () => {
      if (props.usePopup) {
        setLegalText(props.legalObject?.type === LegalType.LegalTerms ? 'terms' : 'privacy');
      } else download();
    };

    const isPrivacy = computed(() => props.legalObject?.type === LegalType.Privacy);

    return {
      typeKey, download, version, onClick, isDownloading, isPrivacy
    };
  }
});
